import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Reel } from "../GameComponents/Reel";
import { Symbol } from "../GameComponents/Symbol";
import { Settings } from "./Settings";
import { Tweens } from "../Components/Effects/Tweens";
import { PlayList } from "../Components/Sounds/PlayList";

@engineComponent(ComponentTypes.DisplayObject)
export class ReelExtended extends Reel {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly OnSpinPostRoll: string = "eventOnSpinPostRoll";

    private playImpact: boolean = false;

    //******************************************************************************************************************
    //  SHOW SYMBOLS
    //******************************************************************************************************************

    public showSymbols(useTransform: boolean = false): void {
        this.shouldBeStopping = false;
        this.isStopping = false;
        this.stoppingCounter = 0;

        this.x = this.positionX;
        this.y = this.positionY;

        while (this.currentSymbols.length > 0) {
            this.returnSymbol(this.currentSymbols.pop());
        }

        for (let i = 0; i < this.symbolBottomCount + this.symbolCount + this.symbolTopCount; i++) {
            let symbolId: number;
            let symbolCase: string;
            let fromStrip: boolean = true;

            if (i < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (i < this.symbolBottomCount + this.symbolCount) {
                if (this.data.transform && this.data.transform.indexOf(Settings.SymbolWild) !== -1) {
                    symbolId = this.data.transform[this.symbolBottomCount + this.symbolCount - i - 1];
                } else {
                    symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - i - 1];
                }
                symbolCase = Symbol.CaseRotation;
                fromStrip = false;
            } else {
                symbolId =
                    this.strip[
                        (this.data.index + this.symbolBottomCount + this.symbolCount - i - 1 + this.strip.length) %
                            this.strip.length
                    ];
                symbolCase = i === this.symbolBottomCount + this.symbolCount ? Symbol.CaseRotation : Symbol.CaseBlur;
            }

            this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, fromStrip));

            this.addChild(this.currentSymbols[0]);
            // this.addChildAt(this.currentSymbols[0], 0);
        }

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }
    }

    //******************************************************************************************************************
    //  SPIN ROLL
    //******************************************************************************************************************

    protected updateReel(): void {
        this.returnSymbol(this.currentSymbols.pop());

        if (this.shouldBeStopping && !this.isStopping) {
            if (this.previousReel === null) {
                this.isStopping = true;
            } else if (this.previousReel.isStopping) {
                this.isStopping = true;
            }
        }

        this.stripIndex = (this.stripIndex - 1 + this.strip.length) % this.strip.length;

        let symbolId = this.strip[this.stripIndex];
        let symbolCase = Symbol.CaseBlur;
        let fromStrip: boolean = true;

        if (this.isStopping) {
            if (this.stoppingCounter < this.symbolBottomCount) {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount - 1 ? Symbol.CaseRotation : Symbol.CaseBlur;
            } else if (this.stoppingCounter < this.symbolBottomCount + this.symbolCount) {
                symbolId = this.data.symbols[this.symbolBottomCount + this.symbolCount - this.stoppingCounter - 1];
                symbolCase = Symbol.CaseRotation;
                fromStrip = false;

                if (
                    symbolId === Settings.SymbolWild ||
                    symbolId === Settings.SymbolScatter ||
                    symbolId === Settings.SymbolScatterDouble
                ) {
                    this.playImpact = true;
                }
            } else {
                symbolId =
                    this.strip[
                        (this.data.index +
                            this.symbolBottomCount +
                            this.symbolCount -
                            this.stoppingCounter -
                            1 +
                            this.strip.length) %
                            this.strip.length
                    ];
                symbolCase =
                    this.stoppingCounter === this.symbolBottomCount + this.symbolCount
                        ? Symbol.CaseRotation
                        : Symbol.CaseBlur;
                fromStrip = false;
            }

            this.stoppingCounter++;
        }

        this.currentSymbols.unshift(this.getSymbol(symbolId - 1, symbolCase, fromStrip));

        this.addChild(this.currentSymbols[0]);

        for (let i = 0; i < this.currentSymbols.length; i++) {
            const symbol = this.currentSymbols[i];
            symbol.x = -symbol.offsetX;
            symbol.y = this.symbolHeight * (i - this.symbolTopCount) - symbol.offsetY;
        }

        if (
            this.isStopping &&
            this.stoppingCounter === this.symbolBottomCount + this.symbolCount + this.symbolTopCount
        ) {
            this.spinPostRoll();
        } else {
            this.spinRoll();
        }
    }

    //******************************************************************************************************************
    //  SPIN POST ROLL
    //******************************************************************************************************************

    protected spinPostRoll(): void {
        PlayList.play("SND_BaseRotationEnd" + this.index);
        let impactSymbols: Array<Symbol> = [];
        if (this.playImpact) {
            // set IMPACT case for scatter symbol
            for (let i = this.symbolTopCount; i < this.currentSymbols.length - this.symbolBottomCount; i++) {
                let symbol = this.currentSymbols[i];
                let symbolId = symbol.id + 1;
                if (
                    symbolId === Settings.SymbolWild ||
                    symbolId === Settings.SymbolScatter ||
                    symbolId === Settings.SymbolScatterDouble
                ) {
                    impactSymbols.push(symbol);

                    let posY = (i - this.symbolTopCount) * this.symbolHeight;
                    Tweens.removeTweens(symbol, "position");
                    Tweens.positionTween(symbol, {
                        tweenX: [this.positionX, this.positionX, this.positionX],
                        tweenY: [posY + this.positionY, posY + this.positionY + this.postRoll, posY + this.positionY],
                        tweenDuration: this.postRollSpeed * 2,
                    });
                    this.currentSymbols[i] = null;
                } else {
                    impactSymbols.push(null);
                }
            }
            this.playImpact = false;
        }

        this.emit(ReelExtended.OnSpinPostRoll, impactSymbols, this.index);
        super.spinPostRoll();
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ReelExtended.ts
 **/